import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";

import BreadCrumb from "Components/Common/BreadCrumb";
import { normalize } from "common/lib/normalize";
import { getAgencyOverlappingPayment } from "common/services/payments.service";
import {
  getOverlapStatusLabel,
  isConfirmedDuplicate,
} from "common/constants/overlap-status";

import OverlappingPaymentDetails from "pages/OverlappingPayments/OverlappingPaymentDetails";

/**
 * Agency-facing detail view. Same content as the admin page, but read-only:
 * resolving an overlap is a CIRT action, so there is no actions menu here.
 */
const ViewAgencyOverlappingPayment = () => {
  const { id } = useParams();

  const [data, setData] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response: any = await getAgencyOverlappingPayment(id, {});
      setData(normalize(response.data));
      setNotFound(false);
    } catch (e: any) {
      setNotFound(true);
      toast.error("Error while fetching the overlapping payment");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const individualName =
    data?.payment?.individual?.fullName ||
    data?.overlappingPayment?.individual?.fullName ||
    "Overlapping Payment";

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Overlapping Payment Details"
          pageTitles={[
            { title: "Dashboard", link: "/" },
            {
              title: "Overlapping Payments",
              link: "/agency/overlapping-payments",
            },
          ]}
          back
        />

        <Row className="justify-content-center">
          <Col xxl={10}>
            <Card>
              {loading ? (
                <CardBody className="p-5 text-center">
                  <Spinner color="primary" />
                </CardBody>
              ) : notFound ? (
                <CardBody className="p-5 text-center">
                  <h5 className="fs-15 mb-1">Overlapping payment not found</h5>
                  <p className="text-muted mb-0">
                    It may have been deleted, or you may not have access to it.
                  </p>
                </CardBody>
              ) : (
                <>
                  <CardHeader className="border-bottom-dashed p-4">
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0 me-3">
                        <div className="avatar-sm">
                          <div className="avatar-title bg-primary-subtle rounded fs-24">
                            <i className="ri-file-copy-2-line text-primary"></i>
                          </div>
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <h5
                          className="fs-15 mb-1"
                          style={{ whiteSpace: "pre-wrap" }}
                        >
                          {individualName}
                        </h5>
                        <div className="d-flex align-items-center flex-wrap gap-2">
                          {data?.resolved ? (
                            <span className="badge bg-success-subtle text-success fs-12">
                              Resolved
                            </span>
                          ) : (
                            <span className="badge bg-danger-subtle text-danger fs-12">
                              Unresolved
                            </span>
                          )}
                          {data?.overlapStatus && (
                            <span
                              className={`badge fs-12 ${
                                isConfirmedDuplicate(data.overlapStatus)
                                  ? "bg-danger-subtle text-danger"
                                  : "bg-success-subtle text-success"
                              }`}
                            >
                              {getOverlapStatusLabel(data.overlapStatus)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardHeader>

                  <CardBody className="p-4">
                    <OverlappingPaymentDetails record={data} />
                  </CardBody>
                </>
              )}
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ViewAgencyOverlappingPayment;
